// ./src/utils/reportConfigs.js
import { numberToMoney } from "./money";

const typeLeadSources = 4; // report types from Controls component
const typeVeluxJobs = 5;
const veluxFrameIds = [5, 6, 7, 9, 10]; // 5 reg sun tnl, 6 low-profile st, 7 impact st, 9 curbmount, 10 deckmount
const cssQtyLine = "#4d93ff";
const cssQtyDot = "#005be6";
const cssValLine = "#00b063";
const cssValDot = "#0f5132";
const cssQtyLine1 = "#ff7f50";
const cssQtyDot1 = "#e65c33";
const cssValLine1 = "#008080";
const cssValDot1 = "#006666";

// Quantity pair
const quantityLineColors = [
	"#4d93ff",
	"#ff7f50",
	"#ff6b6b",
	"#ffb347",
	"#ffd700",
	"#b19cd9",
	"#ff9eb5",
	"#ffcc00",
	"#c5b0ff",
	"#ff8c69",
	"#daa520",
	"#b03060",
];
const quantityDotColors = [
	"#005be6",
	"#e65c33",
	"#cc0000",
	"#cc7a00",
	"#cc9900",
	"#764ba2",
	"#e65c7a",
	"#b8860b",
	"#8a2be2",
	"#cc5200",
	"#996515",
	"#800000",
];

// Value pair
const valueLineColors = [
	"#00b063",
	"#008080",
	"#00e0e0",
	"#9acd32",
	"#00ffff",
	"#228b22",
	"#1e90ff",
	"#00bfff",
	"#32cd32",
	"#40e0d0",
	"#90ee90",
	"#4682b4",
];
const valueDotColors = [
	"#0f5132",
	"#006666",
	"#00b3b3",
	"#669900",
	"#008b8b",
	"#006400",
	"#000080",
	"#0000cc",
	"#2e8b57",
	"#009999",
	"#008000",
	"#191970",
];

export const getReportOptions = (reports) => ({
	responsive: true,
	interaction: {
		mode: "index",
		intersect: false,
	},
	stacked: false,
	plugins: {
		legend: {
			position: "top",
		},
		title: {
			display: true,
			text: "FreeLite Metrics",
		},
	},
	scales: {
		x: { grid: { drawOnChartArea: reports.showXGrid } },
		y: {
			type: "linear",
			display: true,
			position: "left",
			ticks: {
				stepSize: 1,
			},
			grid: {
				drawOnChartArea: reports.showYGrid,
			},
		},
		y1: {
			type: "linear",
			display: true,
			position: "right",
			ticks: {
				callback: (value) => numberToMoney(value, false),
			},
			grid: {
				drawOnChartArea: reports.showY1Grid,
			},
		},
	},
});

const isVelux = (rec, reports) => {
	const currentJob = reports.jobs?.filter((job) => job.id === rec.core_job_data_id)[0];
	const currentOptions = reports.options?.filter(
		(opt) => opt.core_job_data_id === rec.core_job_data_id
	);
	const currentOption = currentOptions.filter((opt) => opt.id === currentJob.options_id)[0];
	const currentJobItems =
		currentOption && reports.jobItems.filter((item) => item.options_id === currentOption.id);

	// test if any of the job items has a velux frame
	if (currentJobItems)
		return currentJobItems?.some((jobItems) => veluxFrameIds.includes(jobItems.frame_types_id));
};

const isLeadSource = (rec, reports, leadSourceId) => {
	const currentJob = reports.jobs?.filter((job) => job.id === rec.core_job_data_id)[0];
	const customer = reports.customers?.filter((c) => c.id === currentJob.customer_id)[0];

	return customer.lead_id === leadSourceId;
};

export const getDatasetTypes = ({ reports, lists }) => {
	const leadSources = lists?.listLeadSources;
	const leadSourceTypes =
		leadSources?.map((source, i) => {
			return {
				name: `leadSource${i}`,
				title: source.text,
				condition: reports.reportTypesId === typeLeadSources,
				filter: (r) => isLeadSource(r, reports, i),
			};
		}) || [];
	return [
		{
			name: "base",
			title: "Base Data",
			condition: reports.includeBase,
			filter: () => true,
		},
		{
			name: "velux",
			title: "VELUX Data",
			condition: reports.reportTypesId === typeVeluxJobs,
			filter: (r) => isVelux(r, reports),
		},
		...leadSourceTypes,
	];
};

export const getDatasetConfigs = ({ reports, standardData, previousData, lists }) => {
	const leadSources = lists?.listLeadSources;
	const leadSourceConfigs =
		leadSources
			?.flatMap((source, i) => [
				{
					condition: reports.showQuantity && reports.reportTypesId === typeLeadSources,
					label: `Qty ${source.text}`,
					data: standardData?.[`leadSource${source.id}`]?.quantity,
					borderColor: quantityLineColors[i],
					backgroundColor: quantityDotColors[i],
					yAxisID: "y",
				},
				{
					condition:
						reports.showQuantity &&
						reports.reportTypesId === typeLeadSources &&
						reports.includePrevious,
					label: `Qty ${source.text} (Previous)`,
					data: previousData?.[`leadSource${source.id}`]?.quantity,
					borderColor: quantityLineColors[i],
					backgroundColor: quantityDotColors[i],
					borderDash: [5, 5],
					yAxisID: "y",
				},
				{
					condition: reports.showValue && reports.reportTypesId === typeLeadSources,
					label: `Value ${source.text}`,
					data: standardData?.[`leadSource${source.id}`]?.value,
					borderColor: valueLineColors[i],
					backgroundColor: valueDotColors[i],
					yAxisID: "y1",
				},
				{
					condition:
						reports.showValue &&
						reports.reportTypesId === typeLeadSources &&
						reports.includePrevious,
					label: `Value ${source.text} (Previous)`,
					data: previousData?.[`leadSource${source.id}`]?.value,
					borderColor: valueLineColors[i],
					backgroundColor: valueDotColors[i],
					borderDash: [5, 5],
					yAxisID: "y1",
				},
			])
			.filter((obj) => obj.condition) || [];
	console.log("leadSourceConfigs:", leadSourceConfigs);

	return [
		// Quantity datasets
		{
			condition: reports.showQuantity && reports.includeBase,
			label: `Quantity`,
			data: standardData?.base.quantity,
			borderColor: cssQtyLine,
			backgroundColor: cssQtyDot,
			yAxisID: "y",
		},
		{
			condition: reports.showQuantity && reports.includeBase && reports.includePrevious,
			label: "Quantity (Previous Year)",
			data: previousData?.base.quantity,
			borderColor: cssQtyLine,
			backgroundColor: cssQtyDot,
			borderDash: [5, 5],
			yAxisID: "y",
		},
		{
			condition: reports.showQuantity && reports.reportTypesId === typeVeluxJobs,
			label: `Qty VELUX`,
			data: standardData?.velux.quantity,
			borderColor: cssQtyLine1,
			backgroundColor: cssQtyDot1,
			yAxisID: "y",
		},
		{
			condition:
				reports.showQuantity && reports.reportTypesId === typeVeluxJobs && reports.includePrevious,
			label: "Qty VELUX (Previous Year)",
			data: previousData?.velux.quantity,
			borderColor: cssQtyLine1,
			backgroundColor: cssQtyDot1,
			borderDash: [5, 5],
			yAxisID: "y",
		},

		// Value datasets
		{
			condition: reports.showValue && reports.includeBase,
			label: `Total Value`,
			data: standardData?.base.value,
			borderColor: cssValLine,
			backgroundColor: cssValDot,
			yAxisID: "y1",
		},
		{
			condition: reports.showValue && reports.includeBase && reports.includePrevious,
			label: "Value (Previous Year)",
			data: previousData?.base.value,
			borderColor: cssValLine,
			backgroundColor: cssValDot,
			borderDash: [5, 5],
			yAxisID: "y1",
		},
		{
			condition: reports.showValue && reports.reportTypesId === typeVeluxJobs,
			label: `Value VELUX`,
			data: standardData?.velux.value,
			borderColor: cssValLine1,
			backgroundColor: cssValDot1,
			yAxisID: "y1",
		},
		{
			condition:
				reports.showValue && reports.reportTypesId === typeVeluxJobs && reports.includePrevious,
			label: "Value VELUX (Previous Year)",
			data: previousData?.velux.value,
			borderColor: cssValLine1,
			backgroundColor: cssValDot1,
			borderDash: [5, 5],
			yAxisID: "y1",
		},
		...leadSourceConfigs,
	];
};
